<template>
    <div class="gm-page pa-4">
        <PageTop :menus="['收益总览', '电价录入', '抄表结算']" />
        <!-- 主体 -->
        <div class="workbench-content">
            <div class="d-flex ga-4 wb-row">
                <!-- 左侧主内容：一个整体面板 -->
                <div class="wb-col wb-col-left">
                    <Tasks />
                    <v-card class="glass-card glass-card--flex wb-brief-card" rounded="xl" elevation="0"
                        :loading="loadingTable">
                        <div class="card-section">
                            <div class="card-header">
                                <div class="card-title-with-icon">
                                    <div class="card-title">昨日运行简报</div>
                                </div>
                                <v-btn class="card-more" variant="text" density="comfortable"
                                    @click="handleGoOperation">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20"
                                        fill="none">
                                        <path
                                            d="M8.57135 15.0001C8.38854 15.0001 8.20573 14.9305 8.06644 14.7907C7.78742 14.5117 7.78742 14.0597 8.06644 13.7807L11.847 10.0001L8.06644 6.21931C7.78742 5.94029 7.78742 5.48828 8.06644 5.20926C8.34546 4.93025 8.79747 4.93025 9.07649 5.20926L13.1097 9.24252C13.5276 9.66038 13.5276 10.3398 13.1097 10.7577L9.07626 14.7907C8.93698 14.9305 8.75416 15.0001 8.57135 15.0001Z"
                                            fill="#333333" fill-opacity="0.5" />
                                        <path
                                            d="M10 1.42857C14.7263 1.42857 18.5714 5.27366 18.5714 10C18.5714 14.7263 14.7263 18.5714 10 18.5714C5.27366 18.5714 1.42857 14.7263 1.42857 10C1.42857 5.27366 5.27366 1.42857 10 1.42857ZM10 0C4.47723 0 0 4.47723 0 10C0 15.5228 4.47723 20 10 20C15.5228 20 20 15.5228 20 10C20 4.47723 15.5228 0 10 0Z"
                                            fill="#333333" fill-opacity="0.5" />
                                    </svg>
                                </v-btn>
                            </div>

                            <div class="run-brief" :class="{ 'run-brief--fill': runBriefShouldFill }">

                                <div class="run-table-wrapper">
                                    <div class="run-table-fill">
                                        <v-table density="compact"
                                            class="bg-transparent flex-grow-1 gm-table run-table-component run-table--brief"
                                            style="width: 100%">
                                            <thead>
                                                <tr>
                                                    <th style="min-width: 200px">电站名称</th>
                                                    <th style="min-width: 120px">所属省市</th>
                                                    <th class="text-end" style="min-width: 100px">收益</th>
                                                    <th style="min-width: 180px">充放电量</th>
                                                    <th style="min-width: 80px">告警总数</th>
                                                    <th style="min-width: 80px">效率</th>
                                                    <th style="min-width: 150px">等效循环次数</th>
                                                    <th style="min-width: 110px">电价类型</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr v-for="(row, idx) in runTable"
                                                    :key="row.stationId ?? row.id ?? idx">
                                                    <td>
                                                        <div class="run-table-station-name" :title="row.name">{{
                                                            row.name }}</div>
                                                        <div class="run-table-station-meta">
                                                            <span>{{ (Number(row.maxPower ?? 0) / 1000).toFixed(2)
                                                            }}</span>
                                                            <span>MW</span>
                                                            <span> / </span>
                                                            <span>{{ (Number(row.capacity ?? 0) / 1000).toFixed(2)
                                                            }}</span>
                                                            <span>MWh</span>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        {{ (row.province || '') + (row.city ? '·' + row.city : '') }}
                                                    </td>
                                                    <td class="text-end">
                                                        {{ formatMoney(row.yesterdayIncome).value +
                                                            formatMoney(row.yesterdayIncome).unit }}
                                                    </td>
                                                    <td>
                                                        <div class="run-table-cell--two-lines">
                                                            <span class="unit-text">充电量:</span> {{
                                                                formatNumber(row.yesterdayPositive).value +
                                                                formatNumber(row.yesterdayPositive).unit }}
                                                        </div>
                                                        <div class="run-table-cell--two-lines">
                                                            <span class="unit-text">放电量:</span> {{
                                                                formatNumber(row.yesterdayReverse).value +
                                                                formatNumber(row.yesterdayReverse).unit }}
                                                        </div>
                                                    </td>
                                                    <td>
                                                        {{ row.alarmNum }} <span class="unit-text">条</span>
                                                    </td>
                                                    <td>
                                                        {{ fetchPower(row.yesterdayPositive, row.yesterdayReverse) }}
                                                    </td>
                                                    <td>
                                                        {{ fetchCycleCount(row.yesterdayPositive, row.capacity) }}
                                                        <span class="unit-text">次</span>
                                                    </td>
                                                    <td>{{ fetchIncomeModel(row.incomeModel) }}</td>
                                                </tr>
                                            </tbody>
                                        </v-table>
                                    </div>
                                    <TablePagination v-model="runTableCurrentPage" :page-size="runTablePageSize"
                                        :total="totalStations" @change="() => fetchStationList()" />
                                </div>
                            </div>
                        </div>
                    </v-card>
                </div>

                <!-- 右侧统计：三块面板，整体与左侧对齐 -->
                <div class="wb-col wb-col-right">
                    <v-card class="glass-card glass-card--flex glass-card--flex-large wb-right-income" rounded="xl"
                        elevation="0">
                        <div class="card-header">
                            <div class="card-title-with-icon">
                                <div class="card-title">收益统计</div>
                            </div>
                        </div>
                        <div class="income-head">
                            <div class="income-item">
                                <div class="income-item-icon">
                                    <img src="@/assets/income_icon.png" alt="">
                                </div>
                                <div class="income-item-content">
                                    <div class="income-label">昨日收益</div>
                                    <div class="income-value">
                                        <span class="income-num">{{ formatMoney(income.yesterdayIncomeTotal).value
                                            }}</span>
                                        <span class="income-unit">{{ formatMoney(income.yesterdayIncomeTotal).unit
                                            }}</span>
                                    </div>
                                </div>
                            </div>
                            <div class="income-item">
                                <div class="income-item-icon">
                                    <img src="@/assets/income_icon.png" alt="">
                                </div>
                                <div class="income-item-content">
                                    <div class="income-label">累计收益</div>
                                    <div class="income-value">
                                        <span class="income-num">{{ formatMoney(income.sumIncome).value }}</span>
                                        <span class="income-unit">{{ formatMoney(income.sumIncome).unit }}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="chart-box">
                            <VChart class="chart" :option="incomeBarOption" autoresize />
                        </div>
                    </v-card>

                    <v-card class="glass-card glass-card--flex glass-card--flex-large wb-right-complete" rounded="xl"
                        elevation="0">
                        <div class="card-header">
                            <div class="card-title-with-icon">
                                <div class="card-title">充放统计</div>
                            </div>
                        </div>
                        <div class="complete-head">
                            <div class="complete-item">
                                <div class="complete-label">
                                    昨日总充电量
                                </div>
                                <div class="complete-value">
                                    <span class="complete-num-small">{{
                                        formatNumber(complete.yesterdayChargeTotal).value }}</span>
                                    <span class="complete-unit">{{ formatNumber(complete.yesterdayChargeTotal).unit
                                    }}</span>
                                </div>
                                <div class="complete-label">
                                    昨日总放电量
                                </div>
                                <div class="complete-value">
                                    <span class="complete-num-small">{{
                                        formatNumber(complete.yesterdayDischargeTotal).value }}</span>
                                    <span class="complete-unit">{{ formatNumber(complete.yesterdayDischargeTotal).unit
                                    }}</span>
                                </div>
                            </div>
                            <div class="complete-item">
                                <div class="complete-label">
                                    累计总充电量
                                </div>
                                <div class="complete-value">
                                    <span class="complete-num-small">{{ formatNumber(complete.sumCharge).value }}</span>
                                    <span class="complete-unit">{{ formatNumber(complete.sumCharge).unit }}</span>
                                </div>
                                <div class="complete-label">
                                    累计总放电量
                                </div>
                                <div class="complete-value">
                                    <span class="complete-num-small">{{ formatNumber(complete.sumDisCharge).value
                                    }}</span>
                                    <span class="complete-unit">{{ formatNumber(complete.sumDisCharge).unit }}</span>
                                </div>
                            </div>
                        </div>
                        <div class="chart-box" style="height: 220px;">
                            <VChart class="chart" :option="completeLineOption" autoresize />
                        </div>
                    </v-card>

                    <v-card class="glass-card glass-card--flex glass-card--flex-large wb-right-alarm" rounded="xl"
                        elevation="0">
                        <div class="card-header">
                            <div class="card-title-with-icon">
                                <div class="card-title">昨日告警统计</div>
                            </div>
                        </div>

                        <div class="alarm-content">
                            <div class="chart-box-small">
                                <VChart class="chart" :option="alarmPieOption" autoresize />
                            </div>
                            <div class="alarm-legend">
                                <div v-for="(item, index) in alarmLegendList" :key="item.name" class="alarm-legend-item"
                                    :class="{ 'is-selected': alarmLegendSelected === index }"
                                    @click="alarmLegendSelected = index">
                                    <span class="alarm-legend-dot" :style="{ background: item.color }"></span>
                                    <span class="alarm-legend-name">{{ item.name }}</span>
                                    <span class="alarm-legend-count">{{ item.value }}条</span>
                                    <span class="alarm-legend-pct">{{ item.percent }}%</span>
                                </div>
                            </div>
                        </div>
                    </v-card>
                </div>
            </div>
        </div>
        <el-drawer v-model="drawer" direction="rtl" size="50%">
            <template #header>
                <h4>历史待办记录</h4>
            </template>
            <template #default>
                <!-- 这里时历史待办列表，顶部有日期、关键字、类型（告警、日报）筛选条件，下面是带分页的列表 -->
                <div class="history-todo-filter"
                    style="margin-bottom: 16px; display: flex; align-items: center; gap: 16px;">
                    <el-date-picker v-model="historyFilter.date" type="daterange" range-separator="至"
                        start-placeholder="开始日期" end-placeholder="结束日期" style="width: 250px;" />
                    <el-input v-model="historyFilter.message" placeholder="请输入关键字" clearable
                        prefix-icon="el-icon-search" style="width: 150px;" />
                    <el-select v-model="historyFilter.type" clearable placeholder="请选择类型" style="width: 150px;">
                        <el-option label="全部" :value="0" />
                        <el-option label="告警" :value="2" />
                        <el-option label="日报" :value="1" />
                    </el-select>
                    <el-button type="primary" @click="fetchHistoryTodoList">查询</el-button>
                </div>
                <el-table :data="historyTodoList" style="width: 100%;" empty-text="暂无数据">
                    <el-table-column prop="datetime" label="日期" width="200" />
                    <el-table-column prop="type" label="类型" width="80">
                        <template #default="scope">
                            <span>{{ scope.row.type === 2 ? '告警' : scope.row.type === 1 ? '日报' : '其他' }}</span>
                        </template>
                    </el-table-column>
                    <el-table-column prop="message" label="内容" min-width="200" />
                    <el-table-column prop="isRead" label="状态" width="80">
                        <template #default="scope">
                            <span>{{ scope.row.isRead ? '已读' : '未读' }}</span>
                        </template>
                    </el-table-column>
                    <el-table-column label="操作" width="100">
                        <template #default="scope">
                            <el-button type="text" size="small" @click="handleViewTodo(scope.row)">查看</el-button>
                        </template>
                    </el-table-column>
                </el-table>
                <div class="history-todo-pagination">
                    <TablePagination v-model="historyPage.page" :page-size="historyPage.pageSize"
                        :total="historyPage.total" @change="fetchHistoryTodoList" />
                </div>
            </template>
        </el-drawer>
        <Details v-model:detailsVisible="detailsVisible" :task="detailsTask" />
    </div>
</template>

<script>
export default {
    name: 'WorkbenchIndex'
}
</script>

<script setup>
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import dayjs from 'dayjs'
import { graphic } from 'echarts'
import { getMonthIncomeAndPower, getAlarmStatistics, getStationList, getTodoList, getStationData, switchTodoReadStatus } from '@/api/workbench/index.js'
import Details from '../components/details.vue'
import PageTop from '@/components/PageTop/index.vue'
import Tasks from '../components/Tasks/index.vue'
import TablePagination from '@/components/TablePagination/index.vue'

const detailsVisible = ref(false)
/** 详情抽屉：与列表行绑定，用于按该行日期拉取详情接口 */
const detailsTask = ref({})

/** 获取 汇总类数据 */
async function fetchMonthIncomAndPower() {
    // 统计周期：今年年初至今，按月聚合
    const endDate = dayjs().format('YYYY-MM-DD HH:mm:ss');
    const startDate = dayjs().startOf('year').format('YYYY-MM-DD HH:mm:ss');

    // 作为筛选条件对象
    const filterCondition = {
        startTime: startDate,
        endTime: endDate,
        type: 'month'
    };

    // 获取接口数据并更新收入相关数据
    const AllMonthData = await getMonthIncomeAndPower(filterCondition);
    if (AllMonthData.data) {
        console.log(AllMonthData.data)
        income.value.yesterdayIncomeTotal = AllMonthData.data.yesterdayIncomeTotal
        income.value.sumIncome = AllMonthData.data.sum_income
        complete.value.yesterdayChargeTotal = AllMonthData.data.yesterdayPositiveTotal
        complete.value.yesterdayDischargeTotal = AllMonthData.data.yesterdayReverseTotal
        complete.value.sumCharge = AllMonthData.data.sum_positive
        complete.value.sumDisCharge = AllMonthData.data.sum_reverse
        if (AllMonthData.data.list.length > 0) {
            AllMonthData.data.list.forEach(element => {
                inComeChartData.value.xAxisData.push(element.date)
                inComeChartData.value.data.push(element.sum_income)
                chargeAndDischargeChartData.value.chargeData.push(element.sum_positive)
                chargeAndDischargeChartData.value.disChargeData.push(element.sum_reverse)
            });
        }
    }
}

/** 获取告警统计 */
async function fetchAlarmStatistics() {
    const AlarmData = await getAlarmStatistics()
    if (AlarmData.data) {
        try {
            const levelMap = [
                { name: '安全类告警', key: 'safety', color: '#FF5227' },
                { name: '收益类告警', key: 'income', color: '#F3C209' },
                { name: '设备类告警', key: 'device', color: '#00D90B' },
                { name: '其他类告警', key: 'other', color: '#3A68FF' }
            ];

            alarm.value = {
                levels: levelMap.map(item => ({
                    name: item.name,
                    value: Number(AlarmData.data[item.key] ?? 0),
                    color: item.color
                }))
            };
        } catch (e) {
            alarm.value = { levels: [] };
            console.error('告警统计数据格式错误', e);
        }
    }
}
const loadingTable = ref(false)
/** 获取电站列表 */
async function fetchStationList() {
    // 新增分页条件
    const params = {
        pageNum: runTableCurrentPage.value,
        pageSize: runTablePageSize.value
    }
    loadingTable.value = true
    try {
        const stationList = await getStationList(params)
        runTable.value = stationList.data.list
        totalStations.value = stationList.data.total || 0
    } catch (e) {
        console.error('获取电站列表失败', e);
    } finally {
        loadingTable.value = false
    }
}

const runTableCurrentPage = ref(1)
const runTablePageSize = ref(10)
const totalStations = ref(0)
const runBriefShouldFill = computed(
    () => (runTable.value?.length ?? 0) < (runTablePageSize.value ?? 10)
)

const historyFilter = ref({
    date: [dayjs().subtract(7, 'day').toDate(), dayjs().toDate()],
    message: '',
    type: 0
})
const historyTodoList = ref([])
const historyPage = ref({
    page: 1,
    pageSize: 40,
    total: 0
})
const fetchHistoryTodoList = async () => {
    const params = {
        pageNum: historyPage.value.page,
        pageSize: historyPage.value.pageSize,
        state: 0,
        startTime: dayjs(historyFilter.value.date[0]).startOf('day').format('YYYY-MM-DD HH:mm:ss'),
        endTime: dayjs(historyFilter.value.date[1]).endOf('day').format('YYYY-MM-DD HH:mm:ss'),
        message: historyFilter.value.message,
        type: historyFilter.value.type
    }
    const response = await getTodoList(params)
    const data = response?.data
    historyTodoList.value = data?.list ?? []
    if (data?.total != null) historyPage.value.total = data.total
}
const handleViewTodo = (todo) => {
    detailsTask.value = todo && typeof todo === 'object' ? { ...todo } : {}
    detailsVisible.value = true
}

const router = useRouter()
const drawer = ref(false)

const income = ref({
    yesterdayIncomeTotal: 0,
    sumIncome: 0
})

const complete = ref({
    yesterdayChargeTotal: 0,
    yesterdayDischargeTotal: 0,
    sumCharge: 0,
    sumDisCharge: 0,
})
const inComeChartData = ref({
    xAxisData: [],
    data: []
});
const chargeAndDischargeChartData = ref({
    chargeData: [],
    disChargeData: []
});

const alarm = ref({
    levels: []
})

/** 充放电曲线：数据为 kWh；≥1MWh 用 MWh 展示，≥1GWh 用 GWh 展示 */
const KWH_PER_MWH = 1000
const KWH_PER_GWH = 1_000_000

function pickChargeDischargeEnergyScale(maxAbsKwh) {
    const n = Math.abs(Number(maxAbsKwh)) || 0
    if (n >= KWH_PER_GWH) return { divisor: KWH_PER_GWH, unit: 'GWh' }
    if (n >= KWH_PER_MWH) return { divisor: KWH_PER_MWH, unit: 'MWh' }
    return { divisor: 1, unit: 'kWh' }
}

/** 收益柱状图：数据为元；≥1万 万元，≥1亿 亿元（与 details 收益图一致） */
function pickRevenueChartScale(maxAbsYuan) {
    const n = Math.abs(Number(maxAbsYuan)) || 0
    if (n >= 1e8) return { divisor: 1e8, unitName: '亿元' }
    if (n >= 1e4) return { divisor: 1e4, unitName: '万元' }
    return { divisor: 1, unitName: '元' }
}

const alarmLegendSelected = ref(0)

const alarmLegendList = computed(() => {
    const levels = alarm.value.levels || []
    const total = levels.reduce((s, l) => s + l.value, 0)
    return levels.map((l) => ({
        name: l.name,
        value: l.value,
        color: l.color,
        // ECharts pie 模板里的 {d}% 默认会做四舍五入并保留 2 位小数
        percent: total ? ((l.value / total) * 100).toFixed(2) : '0.00'
    }))
})

// 运行简报 - 底部表格
const runTable = ref([])

const last7Days = computed(() => {
    const days = []
    for (let i = 6; i >= 0; i--) {
        days.push(dayjs().subtract(i, 'day').format('MM-DD'))
    }
    return days
})

const incomeBarOption = computed(() => {
    const hasData =
        Array.isArray(inComeChartData.value.xAxisData) &&
        inComeChartData.value.xAxisData.length > 0 &&
        Array.isArray(inComeChartData.value.data) &&
        inComeChartData.value.data.some((v) => v != null && v !== '')
    const xData = hasData ? inComeChartData.value.xAxisData : last7Days.value
    if (!hasData) {
        return {
            grid: { top: 24, bottom: 12, left: 12, right: 12, containLabel: true },
            xAxis: {
                type: 'category',
                data: xData,
                axisTick: { show: false },
                axisLine: { show: true, lineStyle: { color: 'rgba(0, 0, 0, 0.12)' } },
                axisLabel: { color: 'rgba(145, 147, 160, 0.50)', fontSize: 11 }
            },
            yAxis: {
                type: 'value',
                name: '元',
                nameLocation: 'end',
                nameGap: 10,
                nameTextStyle: { color: 'rgba(145, 147, 160, 0.75)', fontSize: 11, padding: [0, 0, 0, 4] },
                axisLine: { show: true, lineStyle: { color: 'rgba(0, 0, 0, 0.12)' } },
                axisTick: { show: false },
                splitLine: { show: true, lineStyle: { color: '#ECE9F1' } },
                axisLabel: { color: 'rgba(145, 147, 160, 0.50)', fontSize: 11 }
            },
            tooltip: { show: true },
            series: []
        }
    }
    const rawIncome = inComeChartData.value.data || []
    const incomeNums = rawIncome.map((v) => Number(v)).filter((v) => Number.isFinite(v))
    const maxIncomeYuan = incomeNums.length ? Math.max(...incomeNums.map((v) => Math.abs(v))) : 0
    const revScale = pickRevenueChartScale(maxIncomeYuan)
    const revDiv = revScale.divisor
    const incomeScaled = rawIncome.map((v) => (Number(v) || 0) / revDiv)

    return {
        grid: { top: 24, bottom: 12, left: 12, right: 12, containLabel: true },
        xAxis: {
            type: 'category',
            data: xData,
            axisTick: { show: false },
            axisLine: { show: false },
            axisLabel: { color: 'rgba(145, 147, 160, 0.50)', fontSize: 11 }
        },
        yAxis: {
            type: 'value',
            name: revScale.unitName,
            nameLocation: 'end',
            nameGap: 10,
            nameTextStyle: { color: 'rgba(145, 147, 160, 0.75)', fontSize: 11, padding: [0, 0, 0, 4] },
            axisLine: { show: false },
            axisTick: { show: false },
            splitLine: { lineStyle: { color: '#ECE9F1' } },
            axisLabel: {
                color: 'rgba(145, 147, 160, 0.50)',
                fontSize: 11,
                formatter: (val) => {
                    const n = Number(val)
                    if (!Number.isFinite(n)) return String(val)
                    if (revDiv === 1) {
                        return Math.abs(n) >= 1000 ? n.toLocaleString('zh-CN') : String(n)
                    }
                    return n.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                }
            }
        },
        tooltip: {
            trigger: 'axis',
            backgroundColor: 'rgba(255, 255, 255, 0.90);',
            borderColor: 'transparent',
            borderRadius: 12,
            padding: [8, 12],
            extraCssText: 'backdrop-filter: blur(4px); box-shadow: 0px 4px 16px rgba(0, 0, 0, 0.08);',
            textStyle: { fontSize: 12 },
            formatter: (params) => {
                if (!params?.length) return ''
                const p = params[0]
                const dateStr = p?.axisValue ?? '--'
                const v = p.value != null ? Number(p.value) : 0
                let valTxt
                if (!Number.isFinite(v)) {
                    valTxt = String(p.value)
                } else if (revDiv === 1) {
                    valTxt = Number.isInteger(v)
                        ? String(v)
                        : v.toLocaleString('zh-CN', { minimumFractionDigits: 0, maximumFractionDigits: 2 })
                } else {
                    valTxt = v.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                }
                const u = revScale.unitName
                return `
                <div style="display:flex;flex-direction:column;gap:6px;color:#2E2E2E;font-size:13px;">
                  <div style="font-weight:500;">${dateStr}</div>
                  <div style="display:flex;align-items:center;gap:6px;font-weight:500;">
                    <span style="display:inline-block;border-radius:10px;width:6px;height:6px;background:#3E86FF;"></span>
                    ${valTxt} ${u}
                  </div>
                </div>`
            }
        },
        series: [
            {
                type: 'bar',
                data: incomeScaled,
                barWidth: 16,
                itemStyle: {
                    borderRadius: [8, 8, 0, 0],
                    color: '#3A68FF'
                }
            }
        ]
    }
})

const completeLineOption = computed(() => {
    const hasData =
        Array.isArray(inComeChartData.value.xAxisData) &&
        inComeChartData.value.xAxisData.length > 0 &&
        (chargeAndDischargeChartData.value.chargeData || []).some((v) => v != null && v !== '') ||
        (chargeAndDischargeChartData.value.disChargeData || []).some((v) => v != null && v !== '')
    const xData =
        Array.isArray(inComeChartData.value.xAxisData) && inComeChartData.value.xAxisData.length > 0
            ? inComeChartData.value.xAxisData
            : last7Days.value
    if (!hasData) {
        return {
            color: ['#00D96E', '#3A68FF'],
            grid: { top: 25, bottom: 28, left: 12, right: 12, containLabel: true },
            xAxis: {
                type: 'category',
                data: xData,
                axisTick: { show: false },
                axisLine: { show: true, lineStyle: { color: 'rgba(0, 0, 0, 0.12)' } },
                axisLabel: { color: 'rgba(145, 147, 160, 0.6)', fontSize: 11 }
            },
            yAxis: {
                type: 'value',
                name: 'kWh',
                nameLocation: 'end',
                nameGap: 10,
                nameTextStyle: { color: 'rgba(145, 147, 160, 0.75)', fontSize: 11, padding: [0, 0, 0, 4] },
                axisLine: { show: true, lineStyle: { color: 'rgba(0, 0, 0, 0.12)' } },
                axisTick: { show: false },
                splitLine: { show: true, lineStyle: { color: 'rgba(0, 0, 0, 0.06)' } },
                axisLabel: { color: 'rgba(145, 147, 160, 0.6)', fontSize: 11 }
            },
            legend: { show: true, top: 0, right: 0 },
            tooltip: { show: true },
            series: []
        }
    }
    const chargeRaw = chargeAndDischargeChartData.value.chargeData || []
    const dischargeRaw = chargeAndDischargeChartData.value.disChargeData || []
    const allKwh = [...chargeRaw, ...dischargeRaw]
        .map((v) => Number(v))
        .filter((v) => Number.isFinite(v))
    const maxAbsKwh = allKwh.length ? Math.max(...allKwh.map((v) => Math.abs(v))) : 0
    const cdScale = pickChargeDischargeEnergyScale(maxAbsKwh)
    const cdDiv = cdScale.divisor
    const chargeScaled = chargeRaw.map((v) => (Number(v) || 0) / cdDiv)
    const dischargeScaled = dischargeRaw.map((v) => (Number(v) || 0) / cdDiv)

    return {
        color: ['#00D96E', '#3A68FF'],
        grid: { top: 25, bottom: 28, left: 12, right: 12, containLabel: true },
        xAxis: {
            type: 'category',
            data: xData,
            axisTick: { show: false },
            axisLine: { show: false },
            axisLabel: { color: 'rgba(145, 147, 160, 0.6)', fontSize: 11 }
        },
        yAxis: {
            type: 'value',
            name: cdScale.unit,
            nameLocation: 'end',
            nameGap: 10,
            nameTextStyle: { color: 'rgba(145, 147, 160, 0.75)', fontSize: 11, padding: [0, 0, 0, 4] },
            axisLine: { show: false },
            axisTick: { show: false },
            splitLine: { lineStyle: { color: 'rgba(0, 0, 0, 0.06)' } },
            axisLabel: {
                color: 'rgba(145, 147, 160, 0.6)',
                fontSize: 11,
                formatter: (val) => {
                    const n = Number(val)
                    if (!Number.isFinite(n)) return String(val)
                    if (cdDiv === 1) return Math.abs(n) >= 1000 ? n.toLocaleString('zh-CN') : String(n)
                    return n.toLocaleString('zh-CN', { minimumFractionDigits: 0, maximumFractionDigits: 2 })
                }
            }
        },
        tooltip: {
            trigger: 'axis',
            backgroundColor: '#fff',
            borderColor: 'transparent',
            borderRadius: 8,
            padding: [12, 14],
            extraCssText: 'box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);',
            textStyle: { color: '#2E2E2E', fontSize: 12 },
            axisPointer: {
                type: 'line',
                lineStyle: { type: 'dashed', color: 'rgba(0, 0, 0, 0.15)' }
            },
            formatter: (params) => {
                if (!params?.length) return ''
                const axisValue = params[0].axisValue
                const dateStr = typeof axisValue === 'string' && axisValue.length >= 10 ? axisValue : axisValue
                let html = `<div style="font-weight:500;margin-bottom:8px;">${dateStr}</div>`
                const u = cdScale.unit
                params.forEach((p) => {
                    const v = p.value != null ? Number(p.value) : 0
                    const txt = Number.isFinite(v) ? v.toFixed(2) : '0'
                    html += `<div style="display:flex;align-items:center;gap:6px;margin-top:4px;">${p.marker}<span>${p.seriesName}: ${txt} ${u}</span></div>`
                })
                return html
            }
        },
        legend: {
            show: true,
            top: 0,
            right: 0,
            itemWidth: 10,
            itemHeight: 10,
            itemGap: 20,
            icon: 'circle',
            textStyle: { color: 'rgba(100, 90, 120, 0.85)', fontSize: 12 },
            data: [
                {
                    name: '充电',
                    itemStyle: {
                        color: 'transparent',
                        borderColor: '#00D96E',
                        borderWidth: 2,
                        shadowBlur: 2,
                        shadowColor: 'rgba(0, 0, 0, 0.08)',
                        shadowOffsetY: 1
                    }
                },
                {
                    name: '放电',
                    itemStyle: {
                        color: 'transparent',
                        borderColor: '#528DFF',
                        borderWidth: 2,
                        shadowBlur: 2,
                        shadowColor: 'rgba(0, 0, 0, 0.08)',
                        shadowOffsetY: 1
                    }
                }
            ]
        },
        series: [
            {
                name: '充电',
                type: 'line',
                smooth: true,
                data: chargeScaled,
                symbol: 'circle',
                symbolSize: 4,
                showSymbol: true,
                emphasis: { focus: 'series', scale: 1.2, itemStyle: { borderColor: '#fff', borderWidth: 2 } },
                lineStyle: { width: 2, color: '#00D96E' },
                itemStyle: { color: '#00D96E' },
                areaStyle: {
                    color: graphic.LinearGradient(0, 0, 0, 1, [
                        { offset: 0, color: 'rgba(0, 217, 110, 0.35)' },
                        { offset: 1, color: 'rgba(0, 217, 110, 0.02)' }
                    ])
                }
            },
            {
                name: '放电',
                type: 'line',
                smooth: true,
                data: dischargeScaled,
                symbol: 'circle',
                symbolSize: 4,
                showSymbol: true,
                emphasis: { focus: 'series', scale: 1.2, itemStyle: { borderColor: '#fff', borderWidth: 2 } },
                lineStyle: { width: 2, color: '#3A68FF' },
                itemStyle: { color: '#3A68FF' },
                areaStyle: {
                    color: graphic.LinearGradient(0, 0, 0, 1, [
                        { offset: 0, color: '#0095FF00' },
                        { offset: 1, color: '#3A68FF66' }
                    ])
                }
            }
        ]
    }
})

const alarmPieOption = computed(() => {
    const levels = alarm.value.levels || []
    return {
        color: ['#FF5227', '#F3C209', '#00D90B', '#3A68FF'],

        legend: { show: false },
        tooltip: {
            trigger: 'item',
            backgroundColor: 'rgba(255, 255, 255, 0.98)',
            borderColor: 'transparent',
            borderRadius: 12,
            padding: [10, 12],
            extraCssText: 'backdrop-filter: blur(4px); box-shadow: 0px 4px 16px rgba(0, 0, 0, 0.08);',
            textStyle: { fontSize: 12, color: '#2E2E2E' },
            formatter: (params) => {
                const name = params?.name || ''
                const value = params?.value ?? 0
                const percentRaw = Number(params?.percent ?? 0)
                const percent = Number.isFinite(percentRaw) ? percentRaw : 0
                if (!name) return ''
                return `
                    <div style="display:flex;flex-direction:column;gap:6px;">
                      <div style="display:flex;align-items:center;gap:8px;font-weight:600;">
                        <span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${params?.color || '#999'};"></span>
                        <span>${name}</span>
                      </div>
                      <div style="display:flex;align-items:center;justify-content:space-between;gap:16px;">
                        <span style="color:rgba(46,46,46,0.75);">告警数</span>
                        <span style="color:#2E2E2E;font-weight:500;">${value} 条</span>
                      </div>
                      <div style="display:flex;align-items:center;justify-content:space-between;gap:16px;">
                        <span style="color:rgba(46,46,46,0.75);">占比</span>
                        <span style="color:#2E2E2E;font-weight:500;">${percent.toFixed(2)}%</span>
                      </div>
                    </div>`
            }
        },
        series: [
            {
                type: 'pie',
                radius: ['30%', '55%'],
                center: ['45%', '55%'],
                avoidLabelOverlap: true,
                percentPrecision: 2,
                itemStyle: {
                    borderColor: 'rgba(255,255,255,0.9)',
                    borderWidth: 1
                },
                label: {
                    show: true,
                    position: 'outside',
                    formatter: '{d}%',
                    color: 'rgba(46, 46, 46, 0.7)',
                    fontSize: 12
                },
                labelLine: {
                    show: true,
                    length: 8,
                    length2: 12,
                    lineStyle: { color: 'rgba(0, 0, 0, 0.15)' }
                },
                emphasis: { scale: false },
                data: levels.map((l) => ({
                    name: l.name,
                    value: l.value,
                    itemStyle: { color: l.color }
                }))
            }
        ]
    }
})

function fetchPower(Positive, Reverse) {
    if (!Positive || !Reverse || isNaN(Number(Positive)) || isNaN(Number(Reverse)) || Number(Positive) === 0) {
        return '--';
    }
    const efficiency = (Number(Reverse) / Number(Positive)) * 100;
    if (!Number.isFinite(efficiency) || efficiency <= 85 || efficiency >= 95) {
        return '--';
    }
    return efficiency.toFixed(1) + '%';
}

function fetchCycleCount(Positive, capacity) {
    if (!Positive || !capacity || isNaN(Number(Positive)) || isNaN(Number(capacity)) || Number(capacity) === 0) {
        return '0.0';
    }
    const result = Number(Positive) / Number(capacity);
    return result.toFixed(1);

}

// 换算收益单位
function formatMoney(num) {
    const n = Number(num ?? 0);
    if (isNaN(n)) return { value: '0.00', unit: '元' };

    const units = [
        { threshold: 100000000, divisor: 100000000, unit: '亿元' },
        { threshold: 10000, divisor: 10000, unit: '万元' }
    ];

    const { divisor, unit } = units.find(u => n >= u.threshold) || { divisor: 1, unit: '元' };
    return {
        value: (n / divisor).toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
        unit
    };
}

// 换算电量单位
function formatNumber(num) {
    if (num == null || isNaN(Number(num))) {
        return { value: '0.00', unit: 'kWh' }
    }
    const n = Number(num)
    let value = n
    let unit = 'kWh'
    if (n >= 1_000_000) {
        value = n / 1_000_000
        unit = 'GWh'
    } else if (n >= 1_000) {
        value = n / 1_000
        unit = 'MWh'
    }
    return {
        value: value.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
        unit
    }
}

function fetchIncomeModel(incomeModel) {

    switch (incomeModel) {
        case 1:
            return '国网电价'
        case 2:
            return '南网电价'
        case 3:
            return '自定义电价'
        case 4:
            return '现货电价'
        case 5:
            return '未知电价'
    }

}

const hasWorkbenchInitialized = ref(false)

function handleGoOperation() {
    router.push('/business-operations')
}

onMounted(async () => {
    if (hasWorkbenchInitialized.value) return
    hasWorkbenchInitialized.value = true

    await fetchMonthIncomAndPower()
    await fetchAlarmStatistics()
    await fetchStationList()
})
</script>

<style scoped lang="scss">
.workbench-page {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-height: 0;
}

.workbench-top {
    height: 88px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 32px;
    padding: 0px 16px 0px 0px;
}

.welcome {
    width: 380px;
    flex-shrink: 0;
}

.welcome-name {
    color: #2E2E2E;
    font-family: 'Gotham';
    font-size: 32px;
    font-style: normal;
    font-weight: 450;
    line-height: 50px;
}

.welcome-subtitle {
    color: rgba(46, 46, 46, 0.50);
    font-family: "HarmonyOS Sans SC";
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
}

.top-content {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 32px;
    min-width: 0;
}

.top-metrics {
    display: flex;
    // align-items: center;
    gap: 40px;
    min-width: 0;
}

.metric {
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 100px;
}

.metric-label {
    color: rgba(46, 46, 46, 0.50);
    font-size: 14px;
    font-family: HarmonyOS Sans SC;
    font-weight: 400;
    word-wrap: break-word
}

.metric-value {
    color: #2E2E2E;
    font-size: 36px;
    font-family: 'HarmonyOS Sans SC', sans-serif;
    font-style: normal;
    font-weight: 300;
    line-height: 30px !important;
}

.metric-num {
    font-size: 36px;
    font-family: 'HarmonyOS Sans SC', sans-serif;
    font-style: normal;
    font-weight: 300;
    line-height: 30px !important;

}

.metric-unit {
    font-size: 14px;
    color: rgba(46, 46, 46, 0.7);
    font-family: 'HarmonyOS Sans SC', sans-serif;
    font-weight: 300;
}

.quick-actions {
    display: flex;
    align-items: flex-start;
    gap: 16px;
    flex-wrap: nowrap;
}

.action {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
}

.action-icon-wrapper {
    width: 48px;
    height: 48px;
    border-radius: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.3s ease;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.action-icon-wrapper:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12);
}

.action-label {
    font-size: 11px;
    color: rgba(46, 46, 46, 0.7);
    font-family: 'HarmonyOS Sans SC', sans-serif;
    font-weight: 300;
    white-space: nowrap;
}

.workbench-content {
    flex: 1;
    display: flex;
    min-height: 0;
    // padding: 16px 16px 16px 0px;
    overflow-y: auto;
    margin-top: 16px;
}

.wb-row {
    flex: 1;
    align-items: stretch;
    min-height: 0;
}

.wb-col {
    display: flex;
    flex-direction: column;
    min-width: 0;
}

.wb-col-left,
.wb-col-right {
    gap: 20px;
}

/* 左侧模块间距更紧凑，避免出现大段留白 */
.wb-col-left {
    flex: 2;
    gap: 16px;
}

.wb-col-left> :first-child {
    flex-shrink: 0;
}

.history-todo-pagination {
    margin-top: 16px;
    width: 100%;

    :deep(.table-pagination) {
        width: 100%;
    }
}

.wb-col-right {
    flex: 1;
    display: flex;
    flex-direction: column;
}

/* 左侧“昨日运行简报”卡片撑满剩余高度，数据少时也与右侧对齐 */
.wb-brief-card {
    flex: 1;
    min-height: 700px;
}

.wb-brief-card .card-section {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
}

.wb-brief-card .run-brief {
    flex: 1;
    min-height: 0;
}

.wb-brief-card .run-brief--fill {
    min-height: 0;
}

.glass-card {
    border-radius: 20px;
    border: 1px solid rgba(255, 255, 255, 0.8);
    background: rgba(255, 255, 255, 0.7);
    backdrop-filter: blur(10px);
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04);
}

.glass-card--fill {
    height: 100%;
    display: flex;
    flex-direction: column;
}

.glass-card--flex {
    flex: 1;
    display: flex;
    flex-direction: column;
}

.glass-card--flex-large {
    flex: 1
}

/* 右侧三张卡片高度配比：收益统计更“矮”，充放更“高”，底部与左列对齐 */
.wb-right-income {
    flex: 0 0 317px;
}

.wb-right-complete {
    flex: 1 1 0;
    min-height: 360px;
}

.wb-right-alarm {
    flex: 0 0 316px;
}

/* 充放统计：让图表区域随卡片高度自适应 */
.wb-right-complete .chart-box {
    flex: 1;
    min-height: 180px;
    height: auto;
}

.glass-card--flex-small {
    flex: 1;
}

.card-section {
    padding-bottom: 10px;
}

.card-section--bordered {
    height: 469px;
}


.card-header {
    padding: 16px 20px 12px 20px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
}

.card-title-with-icon {
    display: flex;
    align-items: center;
    justify-content: left;
}

.title-icon {
    width: 24px;
    height: 24px;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
}

.title-icon.orange {
    background: linear-gradient(135deg, #FF8C42 0%, #FFB347 100%);
}

.title-icon.blue {
    background: linear-gradient(135deg, #4A90D9 0%, #6BA3E0 100%);
}

.title-icon.red {
    background: linear-gradient(135deg, #E85D75 0%, #F08A9B 100%);
}

.card-title {
    color: #2E2E2E;
    font-family: "HarmonyOS Sans SC", sans-serif;
    font-size: 16px;
    font-weight: 500;
    margin-left: 8px;
}

.card-subtitle {
    color: rgba(46, 46, 46, 0.7);
    font-family: "HarmonyOS Sans SC", sans-serif;
    font-size: 14px;
    font-weight: 300;
}

.card-more {
    color: rgba(46, 46, 46, 0.7);
    font-family: "HarmonyOS Sans SC", sans-serif;
    font-size: 12px;
    font-weight: 300;
    text-transform: none;
    min-width: 0px !important;
}

.card-todo-right {
    display: inline-flex;
    color: rgba(46, 46, 46, 0.80);

    text-align: right;
    font-feature-settings: 'liga' off, 'clig' off;
    font-family: "HarmonyOS Sans SC";
    font-size: 14px;
    font-style: normal;
    font-weight: 300;
    line-height: 14px;
}

/* 待办事项样式 - 与设计图一致 */
.todo-tabs {
    display: flex;
    align-items: center;
    gap: 0;
    margin: 0;
    justify-content: flex-end;
}

.todo-tab {
    color: rgba(46, 46, 46, 0.6);
    cursor: pointer;
    transition: color 0.2s;
    position: relative;
    font-size: 14px;
    padding: 0 12px;
    display: inline-flex;
    align-items: center;
    gap: 6px;
}

.todo-tab:first-child {
    padding-left: 0;
}

.todo-tab:hover {
    color: #2655F1;
}

.todo-tab.currentActive {
    color: #2655F1;
    font-weight: 500;
}

/* 全部旁的未读数量角标 */
.todo-tab-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 18px;
    height: 18px;
    padding: 0 5px;
    background: #2655F1;
    color: #fff;
    font-size: 12px;
    font-weight: 500;
    line-height: 1;
    border-radius: 9px;
}

.todo-tab-divider {
    width: 1px;
    height: 12px;
    background: rgba(46, 46, 46, 0.2);
    flex-shrink: 0;
}

.todo-tab-link {
    padding-right: 0;
}

.todo-tab-arrow {
    margin-left: 2px;
    opacity: 0.7;
}

.todo-body {
    padding: 20px 20px;
    margin: 0 20px;
    border: 1px solid #E5EDFF;
    border-radius: 10px;
    overflow: hidden;
}

.todo-list {
    display: flex;
    flex-direction: column;
}

/* 轮播容器：内容区 200px，整体留出底部指示器空间，不裁切 */
.todo-list--carousel {
    min-height: 200px;
    overflow: visible;
    contain: layout;
}

.todo-list--carousel :deep(.el-carousel__container),
.todo-list--carousel :deep(.el-carousel__item) {
    height: 200px !important;
}

.todo-list--carousel :deep(.el-carousel__arrow) {
    display: none !important;
}

/* 底部指示器容器：保证可见 */
.todo-list--carousel :deep(.el-carousel__indicators) {
    position: relative;
    margin-top: 24px;
    display: flex;
    justify-content: left;
}

.todo-list--carousel :deep(.el-carousel__indicator) {
    padding: 0 4px;
}

/* 指示器小圆条样式 */
.todo-list--carousel :deep(.el-carousel__button) {
    width: 10px !important;
    height: 3px !important;
    border-radius: 10px !important;
    background-color: rgba(46, 46, 46, 0.2);
}

.todo-list--carousel :deep(.el-carousel__indicator.is-active .el-carousel__button) {
    background-color: #2655F1;
    width: 16px !important;
}

.todo-item {
    display: flex;
    align-items: center;
    width: 100%;
    height: 38px;
    padding: 0 12px;
    margin-bottom: 5px;
    border-radius: 8px;
    cursor: pointer;
    flex-shrink: 0;
}

/* 已读：浅蓝灰背景 */
.todo-item.is-read {
    background-color: rgba(231, 238, 255, 0.4);
}

/* 未读：白底 */
.todo-item.is-unread {
    background-color: transparent;
}

/* 轮播项 hover 已关闭，避免与 el-carousel 重排冲突导致抖动 */

.todo-icon {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    margin: 0px 10px;
}

.todo-icon.warning {
    background: #D9D9D9;
    color: #D9D9D9;
}

.todo-icon.info {
    background: #2655F1;
    color: #2655F1;
}

.todo-main {
    flex: 1;
    min-width: 0;
}

.todo-title {
    color: #2E2E2E;
    font-family: "HarmonyOS Sans SC", sans-serif;
    font-size: 14px;
    font-weight: 400;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 100%;
}

.todo-time {
    color: rgba(46, 46, 46, 0.5);
    font-size: 12px;
    margin-right: 8px;
}

.todo-status {
    font-size: 12px;
    padding: 4px 10px;
    border-radius: 6px;
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    gap: 4px;
}

.todo-status.read {
    background: #EFF1F5;
    color: rgba(46, 46, 46, 0.6);
}

.todo-status-icon {
    width: 14px;
    height: 14px;
    flex-shrink: 0;
}

.todo-status.unread {
    background: #E1E9FC;
    color: #3A68FF;
}

/* 运行简报统计卡片 */
.run-stats {
    display: flex;
    gap: 12px;
    margin: 0 20px;
    border: 1px solid #DFE8FE;
    border-radius: 10px;
    background-color: #F1F4FD;
}

.run-stat-card {
    flex: 1;
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 14px 16px;

}

.run-stat-card:not(:last-child) {
    border-right: 1px solid #E8EAF2;
}

.run-stat-icon {
    width: 36px;
    height: 36px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
}

.run-stat-icon.blue {
    background: linear-gradient(135deg, #4A90D9 0%, #6BA3E0 100%);
}

.run-stat-icon.green {
    background: linear-gradient(135deg, #5CB85C 0%, #7BC97B 100%);
}

.run-stat-icon.orange {
    background: linear-gradient(135deg, #FF8C42 0%, #FFB347 100%);
}

.run-stat-icon.purple {
    background: linear-gradient(135deg, #8A7CFF 0%, #A99CFF 100%);
}

.run-stat-info {
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.run-stat-label {
    font-size: 11px;
    color: rgba(46, 46, 46, 0.6);
    font-family: "HarmonyOS Sans SC", sans-serif;
}

.run-stat-value {
    font-size: 18px;
    font-weight: 600;
    color: #2E2E2E;
    font-family: "HarmonyOS Sans SC", sans-serif;
}

.run-stat-unit {
    font-size: 11px;
    color: rgba(46, 46, 46, 0.6);
    font-weight: 400;
    margin-left: 2px;
}

.chart-section-title {
    padding: 0 20px;
    font-size: 12px;
    color: rgba(46, 46, 46, 0.7);
    font-family: "HarmonyOS Sans SC", sans-serif;
    margin-bottom: 4px;
}

.chart-box {
    height: 161px;
    padding: 6px;
}

.chart-box--small {
    height: 95px;
    // padding: 6px;
}

.chart {
    width: 100%;
    height: 100%;
}

.income-head {
    padding: 12px 20px 8px 20px;
    display: flex;
    gap: 24px;
}

.complete-head {
    padding: 2px 20px 8px 20px;
    margin-left: 70px;
    display: flex;
    gap: 24px;
}

.income-item {
    flex: 1;
    display: flex;
    align-items: flex-start;
    gap: 12px;
}

.income-item-icon {
    width: 48px;
    height: 48px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 12px;
}

.income-item-icon img {
    width: 32px;
    height: 32px;
    object-fit: contain;
}

.income-item-content {
    flex: 1;
    min-width: 0;
}

.income-label,
.complete-label {
    display: flex;
    align-items: center;
    gap: 4px;
    color: rgba(46, 46, 46, 0.6);
    font-family: "HarmonyOS Sans SC";
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
    margin-top: 8px;
}

.income-value,
.complete-value {
    margin-top: 6px;
    display: flex;
    align-items: baseline;
    gap: 4px;
}

.complete-item {
    flex: 1;
}

.income-icon,
.complete-icon {
    color: rgba(46, 46, 46, 0.4);
}

.income-num,
.complete-num {
    color: #2E2E2E;
    font-family: "HarmonyOS Sans SC";
    font-size: 30px;
    font-style: normal;
    font-weight: 500;
    line-height: normal;
}

.complete-num-small {
    color: #2E2E2E;
    font-family: "HarmonyOS Sans SC";
    font-size: 20px;
    font-style: normal;
    font-weight: 500;
    line-height: normal;
}

.income-unit,
.complete-unit {
    color: #2E2E2E;
    font-family: "HarmonyOS Sans SC";
    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
}

.run-brief {
    padding: 0 20px;
    height: auto;
}

.run-table-fill {
    flex: 1;
    min-height: 0;
}

.run-table-pagination {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin: 10px 0 0;
    padding: 0 16px;
    flex-shrink: 0;
}

.run-brief--fill {
    flex: 1;
    min-height: 0;
    display: flex;
}

.run-brief--fill .run-table-wrapper {
    height: 100%;
    width: 100%;
    min-height: 0;
    display: flex;
    flex-direction: column;
}

.run-brief--fill :deep(.table-pagination) {
    flex-shrink: 0;
    margin-top: auto;
}

.run-table-pagination .pagination-info {
    font-size: 14px;
    color: rgba(46, 46, 46, 0.6);
}

.run-table-pagination .pagination-controls {
    display: flex;
    align-items: center;
}

.run-table-wrapper {
    margin-top: 0;
    border-radius: 10px;
    overflow: hidden;
    height: auto;
}

.run-table--brief :deep(.v-table__wrapper) {
    overflow: visible;
}

.run-table--brief :deep(table) {
    border-collapse: collapse;
    width: 100%;
}

.run-table--brief :deep(thead th),
.run-table--brief :deep(tbody td) {
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
    border-right: none;
}

.run-table--brief :deep(thead th) {
    background: rgba(0, 0, 0, 0.02);
    color: rgba(46, 46, 46, 0.85);
    font-family: "HarmonyOS Sans SC", sans-serif;
    font-size: 14px;
    font-weight: 500;
    padding: 6px 10px;
    white-space: nowrap;
}

.run-table--brief :deep(tbody td) {
    padding: 6px 10px;
    color: rgba(46, 46, 46, 0.85);
    font-family: "HarmonyOS Sans SC", sans-serif;
    font-size: 13px;
}

.run-table--brief :deep(tbody tr) {
    background: #fff;
}

.run-table--brief :deep(tbody tr:nth-child(even)) {
    background: rgba(0, 0, 0, 0.02);
}

.run-table--brief :deep(tbody tr:hover td) {
    background: rgba(230, 243, 255, 0.35) !important;
}

.run-table-station-name {
    color: #2E2E2E;
    font-weight: 500;
    margin-bottom: 0px;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
    width: 200px;
}

.run-table-station-meta {
    color: rgba(46, 46, 46, 0.55);
    font-size: 12px;
    line-height: 1.2;
}

.run-table--brief :deep(.unit-text) {
    color: rgba(46, 46, 46, 0.5);
    font-size: 12px;
}

.run-table-cell--two-lines {
    line-height: 1.25;
}

.status-badge {
    display: inline-block;
    padding: 2px 8px;
    border-radius: 10px;
    font-size: 10px;
    font-weight: 400;
}

.status-badge.running {
    background: rgba(98, 194, 169, 0.15);
    color: #3CBF9A;
}

/* 告警统计 */
.alarm-content {
    display: flex;
    align-items: center;
    padding: 0 20px 16px 20px;
    gap: 8px;
    height: 320px;
}

.chart-box-small {
    flex: 1;
    min-width: 0;
    height: 100%;
}

.alarm-legend {
    display: flex;
    flex-direction: column;
    gap: 10px;
    flex: 1;
    min-width: 0;
}

.alarm-legend-item {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: nowrap;
    padding: 8px 12px;
    background: #F0F4FF;
    border-radius: 8px;
    font-size: 12px;
    cursor: pointer;
    border: 2px solid transparent;
    transition: border-color 0.2s, box-shadow 0.2s;
}

.alarm-legend-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
}

.alarm-legend-name {
    color: rgba(46, 46, 46, 0.85);
    flex: 1;
    white-space: nowrap;
}

.alarm-legend-count {
    color: rgba(46, 46, 46, 0.85);
    font-weight: bolder;
    white-space: nowrap;
    flex: 0 0 64px;
    text-align: right;
}

.alarm-legend-pct {
    color: rgba(46, 46, 46, 0.85);
    margin-left: 4px;
    white-space: nowrap;
    flex: 0 0 56px;
    font-weight: bolder;
    text-align: right;
}

.mt-4 {
    margin-top: 16px;
}

.workbench-content .station-name {
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
    width: 170px;
}

:deep(.el-table tr) {
    background: none !important;
}

:deep(.el-table thead) {
    background: linear-gradient(90deg, rgba(231, 238, 255, 0.60) 0%, rgba(231, 238, 255, 0.20) 100%) !important;
    color: #2E2E2E;
}

/* 小屏幕适配 */
@media (max-width: 1400px),
(max-height: 800px) {

    /* 昨日运行简报表格 - 只保留横向滚动条 */
    .run-table-wrapper {
        overflow-x: auto;
        overflow-y: hidden;
        scrollbar-gutter: stable;
        max-width: 100%;
        height: auto;
    }

    .run-table-fill {
        min-width: 1000px;
        overflow: visible;
    }

    /* 禁止内部表格再产生滚动条 */
    .run-table--brief :deep(.v-table__wrapper) {
        overflow-x: visible;
        overflow-y: visible;
    }

    .run-table--brief :deep(table) {
        min-width: 1000px;
    }

    /* 收益统计数字和单位不换行 */
    .income-value,
    .complete-value {
        white-space: nowrap;
    }

    /* 告警统计图例 - 小屏自适应 */
    .alarm-legend-item {
        padding: 6px 8px;
        gap: 4px;
    }

    .alarm-legend-count,
    .alarm-legend-pct {
        flex: 0 0 auto;
        min-width: 32px;
    }

    .alarm-legend-dot {
        width: 6px;
        height: 6px;
    }
}

@media (min-width: 1401px) and (max-width: 1919px) {

    /* 中等屏幕：表格只保留横向滚动条 */
    .run-table-wrapper {
        overflow-x: auto;
        overflow-y: hidden;
        scrollbar-gutter: stable;
        max-width: 100%;
        height: auto;
    }

    .run-table-fill {
        min-width: 1100px;
        overflow: visible;
    }

    .run-table--brief :deep(.v-table__wrapper) {
        overflow-x: visible;
        overflow-y: visible;
    }

    .run-table--brief :deep(table) {
        min-width: 1100px;
    }

    /* 收益统计数字和单位不换行 */
    .income-value,
    .complete-value {
        white-space: nowrap;
    }

    /* 告警统计图例 - 中等屏幕自适应 */
    .alarm-legend-item {
        padding: 7px 10px;
        gap: 6px;
    }

    .alarm-legend-count,
    .alarm-legend-pct {
        flex: 0 0 auto;
        min-width: 40px;
    }
}
</style>
